import React, { useState } from 'react';
import { Image as ImageIcon, FilePlus } from 'lucide-react';
import FileUpload from '../components/common/FileUpload';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import TaskProgressPanel from '../components/conversion/TaskProgressPanel';
import ConversionHistory from '../components/conversion/ConversionHistory';
import { useConversionProcess } from '../hooks/useConversionProcess';

type FitOption = 'fillPage' | 'fitDocumentToPage' | 'maintainAspectRatio';
type ColorType = 'color' | 'greyscale' | 'blackwhite';

const ImageToPDF: React.FC = () => {
    const [files, setFiles] = useState<File[]>([]);
    const [fitOption, setFitOption] = useState<FitOption>('fillPage');
    const [refreshKey, setRefreshKey] = useState(0);
    const [autoRotate, setAutoRotate] = useState(true);
    const [colorType, setColorType] = useState<ColorType>('color');

    const {
        taskId,
        isCreating,
        processError,
        setProcessError,
        executeTask,
        resetTask,
    } = useConversionProcess();

    const handleFilesSelected = (selectedFiles: File[]) => {
        setFiles(selectedFiles);
        setProcessError(null);
    };

    const handleConvert = async () => {
        if (files.length === 0) {
            setProcessError('请至少选择一张图片');
            return;
        }

        await executeTask(files, 'image_to_pdf', {
            fitOption,
            autoRotate,
            colorType,
        });
    };

    const handleReset = () => {
        resetTask();
        setFiles([]);
    };

    const fitOptions = [
        { value: 'fillPage' as const, label: '填充页面', description: '图片填充整个页面' },
        { value: 'fitDocumentToPage' as const, label: '适应页面', description: '调整页面大小适应图片' },
        { value: 'maintainAspectRatio' as const, label: '保持比例', description: '保持图片原始比例' },
    ];

    const colorOptions = [
        { value: 'color' as const, label: '彩色', description: '保留原始颜色' },
        { value: 'greyscale' as const, label: '灰度', description: '转为灰度图像' },
        { value: 'blackwhite' as const, label: '黑白', description: '纯黑白图像' },
    ];

    return (
        <div className="image-to-pdf-page">
            <div className="container">
                <div className="page-header">
                    <h1>图片转 PDF</h1>
                    <p>将多张图片合并为一个 PDF 文件</p>
                </div>

                <div className="page-content">
                    <Card glass>
                        {!taskId ? (
                            <>
                                <div className="upload-section">
                                    <FileUpload
                                        onFilesSelected={handleFilesSelected}
                                        multiple={true}
                                        maxFiles={50}
                                        accept="image/*"
                                    />
                                </div>

                                {files.length > 0 && (
                                    <>
                                        <div className="form-group">
                                            <label className="form-label">
                                                <ImageIcon size={20} style={{ display: 'inline', marginRight: '8px' }} />
                                                页面适配方式
                                            </label>
                                            <div className="compression-options">
                                                {fitOptions.map((option) => (
                                                    <label
                                                        key={option.value}
                                                        className={`compression-option ${fitOption === option.value ? 'selected' : ''}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="fit"
                                                            value={option.value}
                                                            checked={fitOption === option.value}
                                                            onChange={() => setFitOption(option.value)}
                                                            disabled={isCreating}
                                                        />
                                                        <div className="option-content">
                                                            <strong>{option.label}</strong>
                                                            <span>{option.description}</span>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">颜色类型</label>
                                            <div className="compression-options">
                                                {colorOptions.map((color) => (
                                                    <label
                                                        key={color.value}
                                                        className={`compression-option ${colorType === color.value ? 'selected' : ''}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="color"
                                                            value={color.value}
                                                            checked={colorType === color.value}
                                                            onChange={() => setColorType(color.value)}
                                                            disabled={isCreating}
                                                        />
                                                        <div className="option-content">
                                                            <strong>{color.label}</strong>
                                                            <span>{color.description}</span>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={autoRotate}
                                                    onChange={(e) => setAutoRotate(e.target.checked)}
                                                    disabled={isCreating}
                                                />
                                                <span>自动旋转图片以适应页面方向</span>
                                            </label>
                                        </div>

                                        <div className="action-section">
                                            <div className="file-count">
                                                已选择 <strong>{files.length}</strong> 张图片
                                            </div>

                                            <Button
                                                variant="primary"
                                                size="lg"
                                                onClick={handleConvert}
                                                isLoading={isCreating}
                                                icon={<FilePlus />}
                                                disabled={isCreating}
                                            >
                                                {isCreating ? '正在准备转换...' : '开始转换'}
                                            </Button>
                                        </div>
                                    </>
                                )}

                                {processError && (
                                    <div className="message message-error" style={{ marginTop: '15px' }}>
                                        {processError}
                                    </div>
                                )}
                            </>
                        ) : (
                            <TaskProgressPanel taskId={taskId} onReset={handleReset} onComplete={() => setRefreshKey(prev => prev + 1)} />
                        )}
                    </Card>

                    <div className="tips-section">
                        <Card>
                            <h3>💡 使用提示</h3>
                            <ul>
                                <li>支持 JPG、PNG、GIF、BMP 等常见图片格式</li>
                                <li>可以一次上传多张图片，按上传顺序排列</li>
                                <li>每张图片会成为 PDF 的一页</li>
                                <li>建议单张图片不超过 10MB</li>
                                <li>自动旋转功能会根据图片尺寸自动调整方向</li>
                                <li>转换处理可能需要一段时间，请耐心等待</li>
                            </ul>
                        </Card>
                    </div>

                    <ConversionHistory operationFilter="image_to_pdf" onRefreshTrigger={refreshKey} />
                </div>
            </div>
        </div>
    );
};

export default ImageToPDF;
